import { useCallback } from 'react'
import { toast as sonnerToast } from 'sonner'

type ToastId = string | number

export interface Toast {
  id: ToastId
  title?: string
  description?: string
  variant?: 'default' | 'destructive' | 'success'
  duration?: number
}

type ToastInput = Omit<Toast, 'id'>

function showSonnerToast(toast: ToastInput, id?: ToastId): ToastId {
  const { title, description, variant = 'default', duration = 5000, ...props } = toast
  const message = title ?? description ?? ''
  const options = {
    ...props,
    description: title ? description : undefined,
    duration,
    id
  }

  if (variant === 'destructive') {
    return sonnerToast.error(message, options)
  }

  if (variant === 'success') {
    return sonnerToast.success(message, options)
  }

  return sonnerToast(message, options)
}

export function useToast() {
  const toast = useCallback(
    ({ title, description, variant = 'default', duration = 5000, ...props }: ToastInput) => {
      const toastInput: ToastInput = {
        title,
        description,
        variant,
        duration,
        ...props
      }
      const id = showSonnerToast(toastInput)

      return {
        id,
        dismiss: () => {
          sonnerToast.dismiss(id)
        },
        update: (updatedToast: Partial<Toast>) => {
          showSonnerToast({ ...toastInput, ...updatedToast }, id)
        }
      }
    },
    []
  )

  const dismiss = useCallback((toastId?: ToastId) => {
    sonnerToast.dismiss(toastId)
  }, [])

  return {
    toast,
    dismiss,
    toasts: [] as Toast[]
  }
}

export default useToast
